import type { ChannelSlice, ChannelSliceEventBus } from '.';
import { signal } from '@preact/signals-react';
import type { Signal } from '@preact/signals-react';
import { inject, injectable } from 'src/features/ioc';
import type { RootState } from 'src/store';
import { onTabChannelChange, resetChannelData } from 'src/store/actions/widgetAndChannel';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';

/**
 * Implementation of ChannelSlice - a standalone injectable for channel state management.
 *
 * This class is designed to be composed with other slices to build widget functionality.
 * It manages channel state backed by Redux store.
 */
@injectable()
export class ChannelSliceImpl implements ChannelSlice {
    #disposableStack = new DisposableStack();
    #channel!: Signal<string | null>;
    #tabId: string | null = null;
    #storeUnsubscribe: (() => void) | null = null;
    #store: ReactBindings['Store'];

    constructor(@inject('Store') store: ReactBindings['Store']) {
        this.#store = store;
    }

    /**
     * The event bus exposes signals for reading state and events for modifying state.
     *
     * Signals:
     * - `widget::channel`: The current channel ID
     *
     * Events:
     * - `widget::channelChange`: Change the channel (dispatches onTabChannelChange)
     * - `widget::resetChannelData`: Reset channel-specific data (dispatches resetChannelData)
     */
    eventBus: ChannelSliceEventBus = EventBusBuilder.create()
        .addSignal('widget::channel', () => this.#channel)
        .addEvent('widget::channelChange', (payload) => {
            if (this.#tabId) {
                this.#store.dispatch(onTabChannelChange(this.#tabId, payload.channel));
            }
        })
        .addEvent('widget::resetChannelData', () => {
            if (this.#tabId) {
                this.#store.dispatch(resetChannelData(this.#tabId));
            }
        })
        .build();

    /**
     * Initialize the slice with a tab ID.
     *
     * This subscribes to store changes for the channel state and dispatches
     * appropriate actions when events are emitted.
     *
     * @param tabId The tab ID to bind this slice to
     */
    init(tabId: string): void {
        // Clean up existing subscription
        if (this.#storeUnsubscribe) {
            this.#storeUnsubscribe();
            this.#storeUnsubscribe = null;
        }

        this.#tabId = tabId;

        const selectChannelId = (state: RootState) => state.tab.byId[tabId]?.widget?.channelId ?? null;

        this.#channel = signal(selectChannelId(this.#store.getState()));

        // Subscribe to store changes
        this.#storeUnsubscribe = this.#store.subscribeSelector(selectChannelId).handle((channelId) => {
            if (this.#channel.value !== channelId) {
                this.#channel.value = channelId;
            }
        });

        // Register cleanup
        this.#disposableStack.defer(() => {
            if (this.#storeUnsubscribe) {
                this.#storeUnsubscribe();
                this.#storeUnsubscribe = null;
            }
        });
    }

    [Symbol.dispose]() {
        this.#disposableStack.dispose();
    }
}
